import { z } from "zod";

import { ProjectLanguage } from "./enums.js";
import { assertNever } from "./errors.js";
import { AppSessionId } from "./idTypeBrands.js";
import { KernelSize } from "./kernelConfigTypes.js";
import { KernelImage } from "./typeBrands.js";
import { uuid } from "./uuid.js";

export const KernelAppLabelLiteral = z.enum([
  "hex-python-kernel",
  "hex-r-kernel",
]);
export type KernelAppLabelLiteral = z.infer<typeof KernelAppLabelLiteral>;

export const getKernelName = ({
  language,
  prefix,
}: {
  language: ProjectLanguage;
  prefix: string;
}): string => {
  switch (language) {
    case ProjectLanguage.R:
      return `${prefix}-r-kernel`;
    case ProjectLanguage.PYTHON:
      return `${prefix}-python-kernel`;
    default:
      assertNever(language, language);
  }
};

export const SidecarContainerNames = z.enum([
  "hex-kernel-data-service",
  "hex-minerva",
]);
export type SidecarContainerNames = z.infer<typeof SidecarContainerNames>;

export const getKernelApp = ({
  language,
}: {
  language: ProjectLanguage;
}): KernelAppLabelLiteral =>
  KernelAppLabelLiteral.parse(getKernelName({ language, prefix: "hex" }));

export const getFullKernelName = ({
  kernelImage,
  kernelSize,
  language,
  prefix,
}: {
  language: ProjectLanguage;
  prefix: string;
  kernelImage: KernelImage;
  kernelSize: KernelSize;
}): string => {
  const suffix = uuid().replace(/-/gi, "");
  return `${getKernelName({
    language,
    prefix,
  })}-${kernelImage}-${kernelSize}-${suffix}`;
};

export const getKernelGenerateName = ({
  kernelImage,
  kernelSize,
  language,
  prefix,
}: {
  language: ProjectLanguage;
  prefix: string;
  kernelImage: KernelImage;
  kernelSize: KernelSize;
}): string => {
  return `${getKernelName({
    language,
    prefix,
  })}-${kernelImage}-${kernelSize}-`;
};

// prewarm pool pods come from the `<release>-prewarmed-kernels-...` Deployment,
// so this substring is in their name. on-demand pods are named via
// getFullKernelName and never contain it. the name is immutable, so unlike the
// `prewarmed` label (which claim flips to "false") it survives claiming.
export const PREWARM_POOL_POD_NAME_MARKER = "prewarmed-kernels";

export const isPrewarmPoolPodName = (podName: string): boolean =>
  podName.includes(PREWARM_POOL_POD_NAME_MARKER);

/**
 * Operations carrying this id did not come from a person acting in the product.
 * They were replayed from the kernel container's filesystem, which arbitrary
 * user code can write to. They are attributed to the session owner because the
 * kernel runs on their behalf, but that user did not necessarily perform the
 * change.
 */
export const KERNEL_MP_CLIENT_ID_PREFIX = "KERNEL-";

export const getKernelMpClientId = (appSessionId: AppSessionId): string =>
  `${KERNEL_MP_CLIENT_ID_PREFIX}${appSessionId}`;

export const isKernelMpClientId = (mpClientId: string): boolean =>
  mpClientId.startsWith(KERNEL_MP_CLIENT_ID_PREFIX);

/**
 * Applied by the server when a client that is not the app session's own API
 * client submits an operation claiming {@link KERNEL_MP_CLIENT_ID_PREFIX}.
 */
export const SPOOFED_KERNEL_MP_CLIENT_ID_PREFIX = "SPOOFED-";

export function createPodHostnameAndSubdomain({
  appSessionId,
  releaseName,
}: {
  appSessionId: AppSessionId;
  releaseName: string;
}): { hostname: string; subdomain: string } {
  return {
    hostname: `hex-kernel-for-${appSessionId}`,
    subdomain: `${releaseName}-kernel`,
  };
}

export const KernelManagementCPMigrationState = z.object({
  compareBackup: z.boolean(),
  primary: z.enum(["K8s", "CP"]),
});

export type KernelManagementCPMigrationState = z.infer<
  typeof KernelManagementCPMigrationState
>;

export const DefaultKernelManagementCPMigrationState: KernelManagementCPMigrationState =
  { primary: "CP", compareBackup: false };
